/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { MonitorAgent, getMonitorAgentResourceHistory } from "@/api/monitor";
import { useMonitorAgent } from "@/hooks/useMonitorAgent";
import { useServerDetail } from "@/hooks/useServerDetail";
import { Sparkline } from "@/components/common/Sparkline";
import { formatters } from "@/utils/timeSettings";
import { CHART_COLORS } from "@/constants/chartColors";
import { formatBytes } from "@/utils/formatBytes";

function pct(n: number | undefined) {
  return n === undefined ? "—" : `${Math.round(n)}%`;
}
function barClass(n: number) {
  return n >= 90 ? "gh-bar--crit" : n >= 75 ? "gh-bar--warn" : "";
}
function shortModel(model: string | undefined) {
  if (!model) return "";
  return model
    .replace(/\(R\)|\(TM\)|CPU|Processor|\d+-Core/gi, "")
    .replace(/@.*$/, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 22);
}
function uptime(seconds: number | undefined) {
  if (!seconds) return "";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  return d > 0 ? `up ${d}d ${h}h` : `up ${h}h`;
}

export interface AgentWidgetProps {
  agent?: MonitorAgent;
  agents?: MonitorAgent[];
  onAgentChange?: (id: number) => void;
}

/** Header right-slot: a picker when there is more than one agent, plain text
 *  (agent name) otherwise. */
export const AgentSlot: React.FC<{
  agent: MonitorAgent;
  agents?: MonitorAgent[];
  onAgentChange?: (id: number) => void;
}> = ({ agent, agents, onAgentChange }) => {
  if (agents && agents.length > 1 && onAgentChange) {
    return (
      <select
        className="gh-agent-select"
        value={agent.id}
        onChange={(e) => onAgentChange(Number(e.target.value))}
        aria-label="Server"
      >
        {agents.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </select>
    );
  }
  return <span className="gh-sub">{agent.name}</span>;
};

export const NoAgent: React.FC<{ label: string }> = ({ label }) => (
  <section className="gh-w">
    <div className="gh-w-head">
      <span className="gh-label">{label}</span>
    </div>
    <div className="gh-empty">no agent &mdash; add one on the Servers tab</div>
  </section>
);

export const ResourcesWidget: React.FC<AgentWidgetProps> = ({ agent, agents, onAgentChange }) => {
  if (!agent) return <NoAgent label="Resources" />;
  return (
    <ResourcesInner agent={agent} agents={agents} onAgentChange={onAgentChange} />
  );
};

const ResourcesInner: React.FC<{
  agent: MonitorAgent;
  agents?: MonitorAgent[];
  onAgentChange?: (id: number) => void;
}> = ({ agent, agents, onAgentChange }) => {
  const { hardwareStats, systemInfo } = useMonitorAgent({
    agent,
    includeHardwareStats: true,
    includeSystemInfo: true,
  });
  const { data: history = [] } = useQuery({
    queryKey: ["monitor-agent-resource-history", agent.id],
    queryFn: () => getMonitorAgentResourceHistory(agent.id, 1),
    enabled: agent.enabled,
  });

  const cpu = hardwareStats?.cpu?.usage_percent ?? 0;
  const mem = hardwareStats?.memory?.used_percent ?? 0;
  const disk = hardwareStats?.disks?.[0];
  const disk2 = hardwareStats?.disks?.[1];
  const cpuHistory = history.map((p) => p.cpuUsagePercent);
  const memHistory = history.map((p) => p.memoryUsedPercent);
  const heads = history.map((p) => formatters.time(new Date(p.createdAt)));
  const peakCpu = cpuHistory.length ? Math.max(...cpuHistory) : cpu;
  const peakMem = memHistory.length ? Math.max(...memHistory) : mem;
  const load = hardwareStats?.cpu?.load_avg;
  const kernel = systemInfo?.kernel?.split("-")[0];

  return (
    <section className="gh-w">
      <div className="gh-w-head">
        <span className="gh-label">Resources</span>
        <AgentSlot agent={agent} agents={agents} onAgentChange={onAgentChange} />
      </div>

      <div className="gh-res">
        <div className="gh-res-row">
          <div className="gh-res-k">
            CPU
            <small>{hardwareStats?.cpu?.cores ?? "?"}c · {shortModel(hardwareStats?.cpu?.model)}</small>
          </div>
          <div className="gh-bar"><i className={barClass(cpu)} style={{ width: `${cpu}%` }} /></div>
          <div className="gh-res-v">{pct(cpu)}</div>
        </div>
        <div className="gh-res-row">
          <div className="gh-res-k">
            Memory
            <small>
              {hardwareStats?.memory
                ? `${formatBytes(hardwareStats.memory.used)} / ${formatBytes(hardwareStats.memory.total)}`
                : ""}
            </small>
          </div>
          <div className="gh-bar"><i className={barClass(mem)} style={{ width: `${mem}%` }} /></div>
          <div className="gh-res-v">{pct(mem)}</div>
        </div>
        {disk && (
          <div className="gh-res-row">
            <div className="gh-res-k">
              Disk {disk.path}
              <small>{disk.fstype} · {formatBytes(disk.total)}</small>
            </div>
            <div className="gh-bar"><i className={barClass(disk.used_percent)} style={{ width: `${disk.used_percent}%` }} /></div>
            <div className="gh-res-v">{pct(disk.used_percent)}</div>
          </div>
        )}
        {disk2 && (
          <div className="gh-res-row">
            <div className="gh-res-k">
              Disk {disk2.path}
              <small>{disk2.fstype} · {formatBytes(disk2.total)}</small>
            </div>
            <div className="gh-bar"><i className={barClass(disk2.used_percent)} style={{ width: `${disk2.used_percent}%` }} /></div>
            <div className="gh-res-v">{pct(disk2.used_percent)}</div>
          </div>
        )}
      </div>

      <div className="gh-spark gh-net-chart">
        <div className="gh-chart-head">
          <span>CPU · last hour</span>
          <span>peak {Math.round(peakCpu)}%</span>
        </div>
        <Sparkline
          values={cpuHistory}
          color={CHART_COLORS.cpu}
          height={34}
          smooth={2}
          name="cpu"
          format={(v) => `${v.toFixed(0)}%`}
          heads={heads}
          emptyLabel="no history yet"
        />
        <div className="gh-chart-head" style={{ marginTop: 8 }}>
          <span>Memory · last hour</span>
          <span>peak {Math.round(peakMem)}%</span>
        </div>
        <Sparkline
          values={memHistory}
          color="#c98a5b"
          height={34}
          smooth={2}
          name="memory"
          format={(v) => `${v.toFixed(0)}%`}
          heads={heads}
          emptyLabel="no history yet"
        />
      </div>

      <div className="gh-load">
        {load && load.length === 3 && (
          <>
            load avg{" "}
            {load.map((n, i) => (
              <React.Fragment key={i}>
                {i > 0 ? " " : ""}
                <b>{n.toFixed(2)}</b>
              </React.Fragment>
            ))}
          </>
        )}
        {(kernel || systemInfo?.uptime) && (
          <>
            {load && load.length === 3 ? " · " : ""}
            {kernel ? `${kernel} · ` : ""}
            {uptime(systemInfo?.uptime)}
          </>
        )}
      </div>
    </section>
  );
};

export const NetworkWidget: React.FC<AgentWidgetProps> = ({ agent, agents, onAgentChange }) => {
  if (!agent) return <NoAgent label="Network" />;
  return <NetworkInner agent={agent} agents={agents} onAgentChange={onAgentChange} />;
};

const NetworkInner: React.FC<{
  agent: MonitorAgent;
  agents?: MonitorAgent[];
  onAgentChange?: (id: number) => void;
}> = ({ agent, agents, onAgentChange }) => {
  const { status, peakStats, systemInfo } = useMonitorAgent({
    agent,
    includeSystemInfo: true,
    includePeakStats: true,
  });

  const live = status?.liveData;
  // Rolling traffic history from the agent's detail poll (same query the
  // Processes / Disk widgets use, so it costs nothing extra).
  const { history } = useServerDetail(agent);
  // The first point has no previous sample to diff against (reads as 0).
  const rxHist = history.slice(1).map((p) => p.rx);
  const txHist = history.slice(1).map((p) => p.tx);
  const rx = live?.rx?.ratestring ?? "0 B/s";
  const tx = live?.tx?.ratestring ?? "0 B/s";
  const iface =
    systemInfo?.interfaces &&
    Object.values(systemInfo.interfaces).find((i) => i.is_up && i.link_speed > 0);
  const ifaceName =
    iface && /^(eth|en|eno|ens|enp|wl|wlp|bond|team)/.test(iface.name) ? iface.name : null;

  return (
    <section className="gh-w">
      <div className="gh-w-head">
        <span className="gh-label">Network</span>
        <AgentSlot agent={agent} agents={agents} onAgentChange={onAgentChange} />
      </div>

      <div className="gh-net-now">
        <div className="gh-net-fig gh-net-fig--rx">
          <span className="gh-big">{rx}</span>
          <span>&darr; DOWNLOAD</span>
        </div>
        <div className="gh-net-fig">
          <span className="gh-big">{tx}</span>
          <span>&uarr; UPLOAD</span>
        </div>
      </div>

      <div className="gh-spark gh-net-chart">
        <div className="gh-chart-head">
          <span>&darr; traffic · last {Math.max(1, Math.round((history.length * 5) / 60))} min</span>
          <span>peak {formatBytes(Math.max(0, ...rxHist))}/s</span>
        </div>
        <Sparkline values={rxHist} color={CHART_COLORS.cpu} height={34} smooth={3} name="received" format={(v) => `${formatBytes(v)}/s`} heads={history.slice(1).map((p) => formatters.time(new Date(p.t)))} />
        <div className="gh-chart-head" style={{ marginTop: 8 }}>
          <span>&uarr; traffic</span>
          <span>peak {formatBytes(Math.max(0, ...txHist))}/s</span>
        </div>
        <Sparkline values={txHist} color="#c98a5b" height={34} smooth={3} name="sent" format={(v) => `${formatBytes(v)}/s`} heads={history.slice(1).map((p) => formatters.time(new Date(p.t)))} />
      </div>

      <div className="gh-net-meta">
        <span>{status?.connected ? "live" : "offline"}</span>
        {peakStats && (
          <span>
            peak &darr; {peakStats.peak_rx_string} &uarr; {peakStats.peak_tx_string}
          </span>
        )}
        {iface && (
          <span>
            {ifaceName ? `${ifaceName} · ` : ""}
            {iface.link_speed >= 1000 ? `${iface.link_speed / 1000} Gbit` : `${iface.link_speed} Mbit`}
            {systemInfo?.uptime ? ` · ${uptime(systemInfo.uptime)}` : ""}
          </span>
        )}
      </div>
    </section>
  );
};
